// src/views/FullBookingPage/components/EditDateAndCapacityModal/index.jsx
import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { 
  formatDateToString, 
  createStableLocalDate, 
  formatDateForDisplay,
  getDurationOptions,
  timeToHour,
  hourToTime,
  getPossibleDayValues,
  formatTimeDisplay
} from "../../../utils/dateTimeHelpers";
import styles from "./styles.module.scss";

// ============================================================
// SUB-COMPONENTS
// ============================================================

const SlotSummary = ({ slotCount, t }) => {
  if (slotCount === 0) return null;
  
  return (
    <span className={styles.slotSummaryBadge}>
      {t("booking.compactBookingForm.slotsSelected", { count: slotCount })}
      <span className={styles.checkmark}> ✓</span>
    </span>
  );
};

const CapacityControl = ({ capacity, maxCapacity, personLabel, isCoworking, onCapacityChange, disabled, t }) => {
  const decrease = () => onCapacityChange(Math.max(1, capacity - 1));
  const increase = () => onCapacityChange(Math.min(maxCapacity, capacity + 1));
  
  const getUnitLabel = () => {
    if (capacity === 1) return personLabel;
    return isCoworking ? t("booking.compactBookingForm.stations") : t("booking.compactBookingForm.people");
  };
  
  return (
    <div className={styles.capacityControl}>
      <button 
        className={styles.capacityBtn}
        onClick={decrease}
        disabled={capacity <= 1 || disabled}
        type="button"
      >
        −
      </button>
      <span className={styles.capacityValue}>
        {capacity} {getUnitLabel()}
      </span>
      <button 
        className={styles.capacityBtn}
        onClick={increase}
        disabled={capacity >= maxCapacity || disabled}
        type="button"
      >
        +
      </button>
    </div>
  );
};

// ============================================================
// MAIN COMPONENT
// ============================================================

const EditDateAndCapacityModal = ({ 
  isOpen, 
  onClose, 
  space, 
  formData, 
  isCoworking,
  maxCapacity,
  onSave,
  isSaving,
  t,
  currentLanguage
}) => {
  const [capacity, setCapacity] = useState(isCoworking ? formData.coworking_stations || 0 : formData.capacity || 1);
  const [bookingHours, setBookingHours] = useState([...formData.bookingHours || formData.booking_hours]);
  const [errors, setErrors] = useState({});

  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedDuration, setSelectedDuration] = useState(null);
  const [startTime, setStartTime] = useState(null);
  const [endTime, setEndTime] = useState(null);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [availableSlots, setAvailableSlots] = useState([]);
  const [bookedSlots, setBookedSlots] = useState([]);
  const [availableDurations, setAvailableDurations] = useState([]);
  const [loadingAvailability, setLoadingAvailability] = useState(false);
  const [isAddingSlot, setIsAddingSlot] = useState(false);
  const [overlapError, setOverlapError] = useState(null);
  
  // ✅ Track if overlap error was intentionally set by user
  const overlapErrorRef = useRef(null);
  const isFetchingRef = useRef(false);
  
  const calendarRef = useRef(null);
  const fetchTimeoutRef = useRef(null);
  const isMountedRef = useRef(true);
  const lastFetchedDateRef = useRef(null);

  const durationOptions = space ? getDurationOptions(space.minimal_duration, t) : [];
  const slotCount = bookingHours.length;

  // ============================================================
  // HELPER FUNCTIONS
  // ============================================================
  
  const isWorkingDay = useCallback((date) => {
    if (!space?.opening_hours || space.opening_hours.length === 0) return false;
    const stableDate = createStableLocalDate(date);
    if (!stableDate) return false;
    const jsDayOfWeek = stableDate.getDay();
    const possibleDayValues = getPossibleDayValues(jsDayOfWeek);
    return space.opening_hours.some(slot => {
      const slotDay = Number(slot.day);
      return possibleDayValues.includes(slotDay);
    });
  }, [space?.opening_hours]);

  const getDaySchedule = useCallback((date) => {
    if (!date) return null;
    const stableDate = createStableLocalDate(date);
    if (!stableDate) return null;
    const jsDayOfWeek = stableDate.getDay();
    const possibleDayValues = getPossibleDayValues(jsDayOfWeek);
    return space?.opening_hours?.find(slot => {
      const slotDay = Number(slot.day);
      return possibleDayValues.includes(slotDay);
    });
  }, [space?.opening_hours]);

  const isTimeOverlapping = useCallback((start, end, bookedSlotsList) => {
    if (!bookedSlotsList || bookedSlotsList.length === 0) return false;
    const startHour = timeToHour(start);
    const endHour = timeToHour(end);
    return bookedSlotsList.some(booked => {
      const bookedStart = timeToHour(booked.from);
      const bookedEnd = timeToHour(booked.to);
      return (startHour < bookedEnd && endHour > bookedStart);
    });
  }, []);

  const getAvailableTimeSlots = useCallback((date, duration, bookedSlotsList) => {
    if (!date || !duration) return [];
    if (!isWorkingDay(date)) return [];
    const daySchedule = getDaySchedule(date);
    if (!daySchedule) return [];
    const openingTime = daySchedule.from.slice(0, 5);
    const closingTime = daySchedule.to.slice(0, 5);
    const closingHour = timeToHour(closingTime);
    const allSlots = [];
    let current = openingTime;
    while (timeToHour(current) + duration <= closingHour) {
      allSlots.push(current);
      const [hours, minutes] = current.split(":").map(Number);
      const nextMinutes = minutes + 30;
      const nextHour = hours + Math.floor(nextMinutes / 60);
      const nextMinute = nextMinutes % 60;
      current = `${nextHour.toString().padStart(2, "0")}:${nextMinute.toString().padStart(2, "0")}`;
    }
    return allSlots.filter(slotStart => {
      const slotEnd = hourToTime(timeToHour(slotStart) + duration);
      return !isTimeOverlapping(slotStart, slotEnd, bookedSlotsList);
    });
  }, [isWorkingDay, getDaySchedule, isTimeOverlapping]);

  const fetchBookingsForDate = useCallback(async (date) => {
    if (!date || !space?.id) return [];
    const formattedDate = formatDateToString(date);
    
    if (lastFetchedDateRef.current === formattedDate) {
      return null;
    }
    lastFetchedDateRef.current = formattedDate;
    
    try {
      const { fetchPlaceDates } = await import("services/booking.services");
      const response = await fetchPlaceDates(space.id, formattedDate, formattedDate);
      if (response?.success && response?.data) {
        const booked = [];
        if (response.data.reservations) {
          response.data.reservations.forEach(res => {
            if (res.date === formattedDate) {
              booked.push({ from: res.from.slice(0, 5), to: res.to.slice(0, 5) });
            }
          });
        }
        if (response.data.private_reservations) {
          response.data.private_reservations.forEach(res => {
            if (formattedDate >= res.start_date && formattedDate <= res.end_date) {
              booked.push({ from: res.from.slice(0, 5), to: res.to.slice(0, 5) });
            }
          });
        }
        return booked;
      }
    } catch (err) {
      console.error('[EditDateAndCapacityModal] Error fetching bookings:', err);
    }
    return [];
  }, [space?.id]);

  const getDateStatus = useCallback((date) => {
    if (!date) return 'empty';
    const stableDate = createStableLocalDate(date);
    if (!stableDate) return 'empty';
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const minDate = new Date(today);
    minDate.setDate(today.getDate() + 2);
    if (stableDate < minDate) return 'disabled';
    if (stableDate < today) return 'past';
    const isWorking = isWorkingDay(date);
    if (!isWorking) return 'unavailable';
    const isToday = stableDate.getTime() === today.getTime();
    if (isToday) return 'today';
    return 'available';
  }, [isWorkingDay]);

  const availableDurationOptions = useMemo(() => {
    if (!selectedDate || loadingAvailability) {
      return durationOptions;
    }
    return durationOptions.filter(opt => availableDurations.includes(opt.value));
  }, [durationOptions, selectedDate, availableDurations, loadingAvailability]);

  // ============================================================
  // EFFECTS
  // ============================================================

  useEffect(() => {
    if (!isOpen) {
      lastFetchedDateRef.current = null;
      setIsCalendarOpen(false);
      setOverlapError(null);
      overlapErrorRef.current = null;
      setSelectedDate(null);
      setSelectedDuration(null);
      setStartTime(null);
      setEndTime(null);
      setAvailableSlots([]);
      setAvailableDurations([]);
      isFetchingRef.current = false;
    }
  }, [isOpen]);

  // Load availability when date is selected
  useEffect(() => {
    if (!selectedDate) return;
    if (isFetchingRef.current) return;
    
    if (fetchTimeoutRef.current) clearTimeout(fetchTimeoutRef.current);
    
    fetchTimeoutRef.current = setTimeout(async () => {
      isFetchingRef.current = true;
      setLoadingAvailability(true);
      
      // ✅ ONLY clear overlap error if it wasn't set by the user
      // If overlapErrorRef.current is null, it means the user didn't set it
      if (!overlapErrorRef.current) {
        setOverlapError(null);
      }
      
      const booked = await fetchBookingsForDate(selectedDate);
      if (!isMountedRef.current) {
        isFetchingRef.current = false;
        setLoadingAvailability(false);
        return;
      }
      if (booked === null) {
        isFetchingRef.current = false;
        setLoadingAvailability(false);
        return;
      }
      if (booked && booked.length >= 0) {
        setBookedSlots(booked);
        const availableDurs = [];
        const slotsByDuration = {};
        for (const opt of durationOptions) {
          const slots = getAvailableTimeSlots(selectedDate, opt.value, booked);
          slotsByDuration[opt.value] = slots;
          if (slots.length > 0) availableDurs.push(opt.value);
        }
        setAvailableDurations(availableDurs);
        
        // Reset duration and time when date changes
        setSelectedDuration(null);
        setStartTime(null);
        setEndTime(null);
        setAvailableSlots([]);
      }
      setLoadingAvailability(false);
      isFetchingRef.current = false;
    }, 300);
    
    return () => { 
      if (fetchTimeoutRef.current) {
        clearTimeout(fetchTimeoutRef.current);
        fetchTimeoutRef.current = null;
      }
    };
  }, [selectedDate, durationOptions, getAvailableTimeSlots, fetchBookingsForDate]);

  // Update available slots when duration changes
  useEffect(() => {
    if (selectedDate && selectedDuration && bookedSlots.length >= 0) {
      const slots = getAvailableTimeSlots(selectedDate, selectedDuration, bookedSlots);
      setAvailableSlots(slots);
      setStartTime(null);
      setEndTime(null);
    } else if (selectedDuration === null) {
      setAvailableSlots([]);
      setStartTime(null);
      setEndTime(null);
    }
  }, [selectedDuration, selectedDate, bookedSlots, getAvailableTimeSlots]);

  // Calculate end time when start time is selected
  useEffect(() => {
    if (startTime && selectedDuration) {
      const endHour = timeToHour(startTime) + selectedDuration;
      setEndTime(formatTimeDisplay(hourToTime(endHour)));
    } else {
      setEndTime(null);
    }
  }, [startTime, selectedDuration]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (fetchTimeoutRef.current) {
        clearTimeout(fetchTimeoutRef.current);
        fetchTimeoutRef.current = null;
      }
    };
  }, []);

  // ============================================================
  // EARLY RETURN
  // ============================================================
  if (!isOpen) return null;

  // ============================================================
  // HANDLERS
  // ============================================================

  const handleDateSelect = (date) => {
    const stableDate = createStableLocalDate(date);
    if (!stableDate) return;
    
    // ✅ Clear overlap error and ref when user selects a new date
    setOverlapError(null);
    overlapErrorRef.current = null;
    setSelectedDate(stableDate);
    setStartTime(null);
    setEndTime(null);
    setSelectedDuration(null);
    setBookedSlots([]);
    setAvailableDurations([]);
    setAvailableSlots([]);
    setErrors({});
    lastFetchedDateRef.current = null;
    
    if (fetchTimeoutRef.current) {
      clearTimeout(fetchTimeoutRef.current);
      fetchTimeoutRef.current = null;
    }
    isFetchingRef.current = false;
  };

  const handleDurationSelect = (duration) => {
    // ✅ Clear overlap error when user selects a different duration
    setOverlapError(null);
    overlapErrorRef.current = null;
    setSelectedDuration(duration);
    setStartTime(null);
    setEndTime(null);
    setErrors(prev => ({ ...prev, duration: null, startTime: null }));
  };

  const handleStartTimeSelect = (time) => {
    setStartTime(time);
    setErrors(prev => ({ ...prev, startTime: null }));
    // ✅ DO NOT clear overlap error here - user needs to see it
  };

  const handleAddSlot = () => {
    if (!selectedDate || !startTime || !endTime) {
      setErrors({ slot: t("booking.fullBookingPage.errors.slotRequired") });
      return;
    }

    const formattedDate = formatDateToString(selectedDate);
    const newSlot = {
      date: formattedDate,
      from: startTime,
      to: endTime
    };

    const isOverlapping = bookingHours.some(slot => {
      if (slot.date !== newSlot.date) return false;
      const start1 = timeToHour(slot.from);
      const end1 = timeToHour(slot.to);
      const start2 = timeToHour(newSlot.from);
      const end2 = timeToHour(newSlot.to);
      return (start1 < end2 && end1 > start2);
    });

    if (isOverlapping) {
      // ✅ Set overlap error and track that user set it
      const errorMessage = t("booking.fullBookingPage.errors.slotOverlap");
      setOverlapError(errorMessage);
      overlapErrorRef.current = errorMessage;
      return;
    }

    // ✅ Clear overlap error on successful add
    setOverlapError(null);
    overlapErrorRef.current = null;
    setIsAddingSlot(true);
    setBookingHours([...bookingHours, newSlot]);
    
    setSelectedDate(null);
    setSelectedDuration(null);
    setStartTime(null);
    setEndTime(null);
    setBookedSlots([]);
    setAvailableSlots([]);
    setAvailableDurations([]);
    setErrors({});
    lastFetchedDateRef.current = null;
    setIsCalendarOpen(false);
    setIsAddingSlot(false);
  };

  const handleRemoveSlot = (index) => {
    const updated = [...bookingHours];
    updated.splice(index, 1);
    setBookingHours(updated);
    setErrors({});
    setOverlapError(null);
    overlapErrorRef.current = null;
  };

  const handleSave = () => {
    if (bookingHours.length === 0) {
      setErrors({ slot: t("booking.fullBookingPage.errors.slotRequired") });
      return;
    }

    const newData = {
      capacity: isCoworking ? 0 : capacity,
      coworking_stations: isCoworking ? capacity : 0,
      booking_hours: bookingHours
    };

    onSave(newData);
  };

  const toggleCalendar = () => {
    setIsCalendarOpen(!isCalendarOpen);
    if (!isCalendarOpen) {
      setOverlapError(null);
      overlapErrorRef.current = null;
    }
  };

  // ============================================================
  // RENDER HELPERS
  // ============================================

  const personLabel = isCoworking 
    ? t("booking.compactBookingForm.station") 
    : t("booking.compactBookingForm.person");

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const days = [];
    let firstDayOfWeek = firstDay.getDay();
    firstDayOfWeek = firstDayOfWeek === 0 ? 6 : firstDayOfWeek - 1;
    for (let i = 0; i < firstDayOfWeek; i++) days.push(null);
    for (let i = 1; i <= lastDay.getDate(); i++) days.push(new Date(year, month, i));
    return days;
  };

  const daysInMonth = getDaysInMonth(currentMonth);

  const calculateDuration = (from, to) => {
    if (!from || !to) return 0;
    const fromHour = timeToHour(from);
    const toHour = timeToHour(to);
    return Math.round((toHour - fromHour) * 10) / 10;
  };

  const showSelectionPanel = selectedDate !== null;

  // ============================================================
  // RENDER
  // ============================================================

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h3>{t("booking.fullBookingPage.editBooking.title")}</h3>
          <button className={styles.closeBtn} onClick={onClose}>×</button>
        </div>

        <div className={styles.modalBody}>
          {/* Capacity */}
          <div className={styles.field}>
            <label>
              {isCoworking 
                ? t("booking.fullBookingPage.bookingDetails.workstations")
                : t("booking.fullBookingPage.bookingDetails.people")}
            </label>
            <CapacityControl
              capacity={capacity}
              maxCapacity={maxCapacity}
              personLabel={personLabel}
              isCoworking={isCoworking}
              onCapacityChange={setCapacity}
              disabled={isSaving}
              t={t}
            />
          </div>

          {/* Date/Time Selection */}
          <div className={styles.field}>
            <label>{t("booking.fullBookingPage.bookingDetails.date")}</label>
            
            <div className={styles.datePickerWrapper}>
              <button 
                className={styles.dateButton} 
                onClick={toggleCalendar} 
                type="button"
                disabled={isSaving}
              >
                {slotCount > 0 && !isCalendarOpen ? (
                  <SlotSummary slotCount={slotCount} t={t} />
                ) : selectedDate && startTime && endTime ? (
                  <span className={styles.selectedDate}>
                    {formatDateForDisplay(selectedDate, currentLanguage).dayOfMonth} 
                    {' '}{formatDateForDisplay(selectedDate, currentLanguage).monthYear} 
                    {' • '}{formatTimeDisplay(startTime)} - {formatTimeDisplay(endTime)}
                  </span>
                ) : selectedDate ? (
                  <span className={styles.selectedDate}>
                    {formatDateForDisplay(selectedDate, currentLanguage).dayOfMonth} 
                    {' '}{formatDateForDisplay(selectedDate, currentLanguage).monthYear}
                  </span>
                ) : (
                  <span className={styles.placeholderDate}>
                    {t("booking.compactBookingForm.selectDate")}
                  </span>
                )}
                <svg className={styles.calendarIcon} width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path d="M19 4H5C3.89543 4 3 4.89543 3 6V20C3 21.1046 3.89543 22 5 22H19C20.1046 22 21 21.1046 21 20V6C21 4.89543 20.1046 4 19 4Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M16 2V6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M8 2V6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M3 10H21" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>

              {isCalendarOpen && (
                <div 
                  className={`${styles.calendarPopup} ${showSelectionPanel ? styles.hasDateSelected : ''}`} 
                  ref={calendarRef}
                >
                  <div className={styles.calendarContent}>
                    <div className={styles.calendarSection}>
                      <div className={styles.calendarHeader}>
                        <button onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1))}>‹</button>
                        <span>{currentMonth.toLocaleString(currentLanguage, { month: "long" })} {currentMonth.getFullYear()}</span>
                        <button onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1))}>›</button>
                      </div>
                      <div className={styles.weekdays}>
                        {[
                          t("days.short.monday"),
                          t("days.short.tuesday"),
                          t("days.short.wednesday"),
                          t("days.short.thursday"),
                          t("days.short.friday"),
                          t("days.short.saturday"),
                          t("days.short.sunday")
                        ].map(day => (
                          <span key={day}>{day}</span>
                        ))}
                      </div>
                      <div className={styles.days}>
                        {loadingAvailability && (
                          <div className={styles.loadingOverlay}>
                            <div className={styles.smallSpinner}></div>
                          </div>
                        )}
                        {daysInMonth.map((date, idx) => {
                          if (!date) return <span key={`empty-${idx}`} className={styles.emptyDay} />;
                          
                          const status = getDateStatus(date);
                          const isSelected = selectedDate && date.toDateString() === selectedDate.toDateString();
                          const isDisabled = status === 'disabled' || status === 'past' || status === 'unavailable';
                          
                          return (
                            <button
                              key={`${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`}
                              className={`${styles.day} ${styles[status]} ${isSelected ? styles.selected : ""}`}
                              onClick={() => (status === 'available' || status === 'today') && handleDateSelect(date)}
                              disabled={isDisabled || isSaving}
                              type="button"
                            >
                              {date.getDate()}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {showSelectionPanel && (
                      <div className={styles.selectionSection}>
                        {/* ✅ Overlap error - stays visible until user explicitly clears it */}
                        {overlapError && (
                          <div className={styles.overlapError}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <circle cx="12" cy="12" r="10" />
                              <path d="M12 8V12" strokeLinecap="round" />
                              <path d="M12 16H12.01" strokeLinecap="round" />
                            </svg>
                            <span>{overlapError}</span>
                          </div>
                        )}
                        
                        <div className={styles.selectionRow}>
                          <label>{t("booking.compactBookingForm.duration")}</label>
                          {loadingAvailability ? (
                            <p className={styles.loadingMessage}>{t("booking.compactBookingForm.loadingDurations")}</p>
                          ) : availableDurationOptions.length > 0 ? (
                            <div className={styles.optionsGrid}>
                              {availableDurationOptions.map(opt => (
                                <button
                                  key={opt.value}
                                  className={`${styles.optionChip} ${selectedDuration === opt.value ? styles.selected : ""}`}
                                  onClick={() => handleDurationSelect(opt.value)}
                                  disabled={isSaving}
                                  type="button"
                                >
                                  {opt.label}
                                </button>
                              ))}
                            </div>
                          ) : (
                            <p className={styles.noOptionsMessage}>{t("booking.compactBookingForm.noDurations")}</p>
                          )}
                        </div>

                        {selectedDuration && (
                          <div className={styles.selectionRow}>
                            <label>{t("booking.compactBookingForm.startTime")}</label>
                            {loadingAvailability ? (
                              <p className={styles.loadingMessage}>{t("booking.compactBookingForm.loadingTimeSlots")}</p>
                            ) : availableSlots.length > 0 ? (
                              <div className={styles.optionsGrid}>
                                {availableSlots.map(slot => (
                                  <button
                                    key={slot}
                                    className={`${styles.optionChip} ${startTime === slot ? styles.selected : ""}`}
                                    onClick={() => handleStartTimeSelect(slot)}
                                    disabled={isSaving}
                                    type="button"
                                  >
                                    {slot}
                                  </button>
                                ))}
                              </div>
                            ) : (
                              <p className={styles.noOptionsMessage}>{t("booking.compactBookingForm.noSlots")}</p>
                            )}
                          </div>
                        )}

                        {selectedDuration && startTime && endTime && (
                          <button 
                            className={styles.addSlotButton} 
                            onClick={handleAddSlot}
                            disabled={isSaving || isAddingSlot}
                            type="button"
                          >
                            {isAddingSlot ? t("booking.compactBookingForm.adding") : t("booking.compactBookingForm.addSlot")}
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {errors.slot && !overlapError && <div className={styles.error}>{errors.slot}</div>}
          </div>

          {bookingHours.length > 0 && (
            <>
              <div className={styles.selectedSlotsList}>
                {bookingHours.map((slot, index) => {
                  const dateDisplay = formatDateForDisplay(slot.date, currentLanguage);
                  const duration = calculateDuration(slot.from, slot.to);
                  return (
                    <div key={index} className={styles.selectedSlotItem}>
                      <div className={styles.slotInfoWrapper}>
                        <svg 
                          className={styles.checkmarkIcon} 
                          width="20" 
                          height="20" 
                          viewBox="0 0 24 24" 
                          fill="none"
                        >
                          <circle cx="12" cy="12" r="10" fill="#22c55e" />
                          <path d="M8 12L11 15L16 9" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        <span className={styles.slotInfo}>
                          {dateDisplay.dayOfMonth} {dateDisplay.monthYear}
                          {' • '}{slot.from} - {slot.to}
                          <span className={styles.slotDuration}>({duration}h)</span>
                        </span>
                      </div>
                      <button
                        className={styles.removeSlotBtn}
                        onClick={() => handleRemoveSlot(index)}
                        disabled={isSaving}
                        type="button"
                      >
                        ✕
                      </button>
                    </div>
                  );
                })}
              </div>
              <div className={styles.slotsCount}>
                {bookingHours.length} {bookingHours.length === 1 ? t("booking.compactBookingForm.slot") : t("booking.compactBookingForm.slots")}
              </div>
            </>
          )}
        </div>

        <div className={styles.modalFooter}>
          <button className={styles.cancelBtn} onClick={onClose} disabled={isSaving}>
            {t("buttons.cancel")}
          </button>
          <button 
            className={styles.saveBtn} 
            onClick={handleSave}
            disabled={isSaving}
          >
            {isSaving ? (
              <span className={styles.spinner}></span>
            ) : (
              t("buttons.save")
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditDateAndCapacityModal;